/**
 * Download e aggiornamento automatico dati offline (IndexedDB / Dexie).
 */
import { offlineDb } from './offline-db';
import { upsertOfflineLoginUsersFromDirectory } from './offline-users-store';
import { encryptData, isWebCryptoSubtleAvailable } from './crypto-utils';
import { prefetchMapTilesFromEnv } from './map-tiles-prefetch';
import { getAccessTokenFromStorage } from './session-store';
import {
  fetchOfflineOrganizationConfigs,
  fetchOfflineSyncUsers,
  parseApiCallResponse } from
'./offline-sync-api';
import { pickApiField, pickOrganizationIdFromConfigItem } from './offline-field-utils';

/** @deprecated Usare parseApiCallResponse da offline-sync-api */
const parseApiEnvelope = parseApiCallResponse;

const CONFIG = {
  refreshIntervalMinutes:
  Number.parseInt(process.env.REACT_APP_OFFLINE_REFRESH_INTERVAL_MINUTES || '', 10) || 30,
  /** Dopo un prefetch bbox completato, non ripetere prima di N ore (default 7 giorni). */
  tilePrefetchCooldownHours:
  Number.parseInt(process.env.REACT_APP_OFFLINE_TILE_PREFETCH_COOLDOWN_HOURS || '', 10) || 168
};

let refreshIntervalId = null;
let onlineHandler = null;

/**
 * Bearer token corrente (localStorage).
 * @returns {string | null}
 */
function getToken() {
  return getAccessTokenFromStorage();
}

/**
 * Inizializza sync dopo login: download iniziale, listener online, refresh periodico.
 */
export async function initOfflineDataSync() {
  if (typeof window === 'undefined') return;

  stopOfflineDataSync();

  await syncAllData();

  setupOnlineListener();

  const intervalMs = CONFIG.refreshIntervalMinutes * 60 * 1000;




  refreshIntervalId = window.setInterval(async () => {
    if (navigator.onLine) {

      await syncAllData();
    } else {

    }
  }, intervalMs);
}

/**
 * Sync completo: utenti (criptati), init/config, tiles mappa.
 * @returns {Promise<{ success: boolean, reason?: string, results?: object, error?: string }>}
 */
/**
 * @param {{ organizationIdForConfig?: number | null }} [options]
 */
export async function syncAllData(options = {}) {
  const organizationIdForConfig =
  options.organizationIdForConfig !== undefined ? options.organizationIdForConfig : null;





  if (typeof navigator !== 'undefined' && !navigator.onLine) {

    return { success: false, reason: 'offline' };
  }

  const token = getToken();
  if (!token) {

    return { success: false, reason: 'no_token' };
  }

  const results = {
    users: false,
    config: false,
    organizationsConfigured: 0,
    organizationIdForConfig: organizationIdForConfig ?? null,
    tiles: false,
    userImportStats: null,
    timestamp: new Date().toISOString()
  };

  try {

    const usersStep = await syncOfflineUserDirectory(token);
    results.users = usersStep.ok;
    results.userImportStats = usersStep.importStats ?? null;



    const configStep = await syncOrganizationConfigs(token, organizationIdForConfig);
    results.config = configStep.ok;
    results.organizationsConfigured = configStep.organizationsConfigured ?? 0;






    results.tiles = await syncMapTiles();


    const ts = Date.now();
    await offlineDb.meta.put({ key: 'lastOfflineSync', value: ts });


    return { success: true, results };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);

    return { success: false, error: message };
  }
}

/**
 * Scarica la rubrica utenti per uso offline (`GET .../OfflineSync/users`).
 * Pensata per **tutti i ruoli** (admin, operatori, ecc.): il backend filtra l’ambito per `type`.
 * Se la risposta include `offlinePasswordVerifier` (solo admin), popola anche `offline_login_users`.
 * Non dipende da org-config né tiles: persistenza rubrica criptata + credenziali offline.
 *
 * @param {string} [callerToken] - Se omesso, usa il bearer da `getAccessTokenFromStorage()` (es. dopo login).
 * @returns {Promise<{ ok: boolean, importStats?: import('./offline-users-store').OfflineLoginImportStats }>}
 */
export async function syncOfflineUserDirectory(callerToken) {
  const token = callerToken ?? getToken();
  if (!token) {

    return { ok: false };
  }

  try {

    const api = await fetchOfflineSyncUsers(token);
    if (!api.ok) {

      return { ok: false };
    }

    const users = Array.isArray(api.data) ? api.data : [];

    /** @type {unknown} */
    let payloadToStore;
    if (isWebCryptoSubtleAvailable()) {
      payloadToStore = await encryptData(JSON.stringify(users));

    } else {




      payloadToStore = {
        __adamsOfflineUsersPlain: true,
        json: JSON.stringify(users)
      };

    }

    await offlineDb.offlineData.put({
      key: 'users',
      data: payloadToStore,
      updatedAt: new Date().toISOString()
    });

    const importStats = await upsertOfflineLoginUsersFromDirectory(users);
    if (importStats.imported > 0) {

    } else {





    }

    return { ok: true, importStats };
  } catch (err) {

    return { ok: false };
  }
}

/**
 * Bundle da GET OfflineSync/organization-configs.
 * @param {string} token
 * @param {number | null | undefined} [organizationId] — se valorizzato, query `organization_id`
 * @returns {Promise<{ ok: boolean, organizationsConfigured: number }>}
 */
export async function syncOrganizationConfigs(token, organizationId) {
  try {
    const queryLabel =
    organizationId != null && organizationId !== '' ? `organization_id=${organizationId}` : 'all orgs';

    const api = await fetchOfflineOrganizationConfigs(token, organizationId ?? null);
    if (api.httpStatus === 403) {

      return { ok: false, organizationsConfigured: 0 };
    }
    if (!api.ok || api.data == null || typeof api.data !== 'object') {

      return { ok: false, organizationsConfigured: 0 };
    }

    const bundle = /** @type {{ generatedAt?: string, items?: unknown[] }} */api.data;
    const items = Array.isArray(bundle.items) ? bundle.items : [];

    await offlineDb.offlineData.put({
      key: 'organizationConfigsBundle',
      data: bundle,
      updatedAt: new Date().toISOString()
    });

    let orgSaved = 0;
    for (const item of items) {
      const oid = pickOrganizationIdFromConfigItem(item);
      if (oid == null) continue;
      const record = /** @type {Record<string, unknown>} */
      item && typeof item === 'object' ? item : {};

      const shortDesc = pickApiField(
        record,
        'organizationShortDescription',
        'OrganizationShortDescription',
        'organization_short_description'
      );
      await offlineDb.offlineData.put({
        key: `orgForm_${oid}`,
        data: {
          organizationId: oid,
          organizationShortDescription:
          shortDesc != null ? String(shortDesc) : null,
          configuration: record.configuration ?? record.Configuration,
          defaults: record.defaults ?? record.Defaults
        },
        updatedAt: new Date().toISOString()
      });
      orgSaved++;
    }






    return { ok: true, organizationsConfigured: orgSaved };
  } catch (err) {

    return { ok: false, organizationsConfigured: 0 };
  }
}

/**
 * Prefetch massiccio bbox (migliaia di GET `.pbf` via SW): **disattivo di default**.
 * La mappa online usa solo le tile in vista; questo serve solo a riempire la cache per area offline.
 */
async function shouldPrefetchMapTiles() {
  if (process.env.REACT_APP_OFFLINE_DISABLE_TILE_PREFETCH === 'true') {

    return false;
  }
  if (process.env.REACT_APP_OFFLINE_TILE_PREFETCH_ENABLED !== 'true') {




    return false;
  }
  const rec = await offlineDb.meta.get('lastMapTilePrefetchAt');
  const last = rec?.value != null ? Number(rec.value) : null;
  const cooldownMs = CONFIG.tilePrefetchCooldownHours * 60 * 60 * 1000;
  if (last != null && !Number.isNaN(last) && Date.now() - last < cooldownMs) {




    return false;
  }
  return true;
}

async function syncMapTiles() {
  try {
    const allow = await shouldPrefetchMapTiles();
    if (!allow) {
      return true;
    }
    let lastStep = -1;
    const result = await prefetchMapTilesFromEnv((processed, total) => {
      if (!total) return;
      const step = Math.min(10, Math.floor(processed / total * 10));
      if (step > lastStep || processed === total) {
        lastStep = step;
        const pct = Math.round(processed / total * 100);

      }
    });

    if (result.total > 0 && result.downloaded + result.failed > 0) {
      await offlineDb.meta.put({ key: 'lastMapTilePrefetchAt', value: Date.now() });
    }
    if (result.total === 0) return true;
    return result.failed < result.total;
  } catch (err) {

    return false;
  }
}

function setupOnlineListener() {
  if (onlineHandler) {
    window.removeEventListener('online', onlineHandler);
  }
  onlineHandler = async () => {

    await syncAllData();
  };
  window.addEventListener('online', onlineHandler);

}

/**
 * Ferma refresh periodico e listener online (logout).
 */
export function stopOfflineDataSync() {
  if (refreshIntervalId) {
    clearInterval(refreshIntervalId);
    refreshIntervalId = null;

  }
  if (onlineHandler && typeof window !== 'undefined') {
    window.removeEventListener('online', onlineHandler);
    onlineHandler = null;

  }
}

/**
 * @returns {Promise<unknown[] | null>}
 */
export async function getOfflineUsers() {
  const record = await offlineDb.offlineData.get('users');
  if (!record?.data) return null;
  const d = record.data;
  if (
  typeof d === 'object' &&
  d !== null &&
  '__adamsOfflineUsersPlain' in d &&
  /** @type {{ __adamsOfflineUsersPlain?: boolean, json?: string }} */d.__adamsOfflineUsersPlain === true &&
  typeof /** @type {{ json?: string }} */d.json === 'string')
  {
    return JSON.parse(/** @type {{ json: string }} */d.json);
  }
  const { decryptData } = await import('./crypto-utils');
  const decrypted = await decryptData(
    /** @type {{ iv: string, ciphertext: string }} */d
  );
  return JSON.parse(decrypted);
}

/**
 * Trova il profilo nella rubrica scaricata (`OfflineSync/users`) per username (case-insensitive).
 * Serve al login offline per non riusare `init.user` dello snapshot dell’ultimo login online (es. admin).
 *
 * @param {string} username
 * @returns {Promise<Record<string, unknown> | null>}
 */
export async function findOfflineDirectoryUserByUsername(username) {
  if (typeof username !== 'string' || !username.trim()) return null;
  const list = await getOfflineUsers();
  if (!Array.isArray(list)) return null;
  const want = username.trim().toLowerCase();
  for (const u of list) {
    if (!u || typeof u !== 'object') continue;
    const row = /** @type {Record<string, unknown>} */u;
    const un = row.username ?? row.Username;
    if (un != null && String(un).trim().toLowerCase() === want) {
      return row;
    }
  }
  return null;
}

/**
 * Converte una riga della rubrica offline nel formato `user` usato in Auth/Init.
 *
 * @param {Record<string, unknown> | null | undefined} row
 * @param {Record<string, unknown> | null | undefined} snapshotInitUser - fallback (es. theme)
 * @returns {Record<string, unknown> | null}
 */
export function mapDirectoryRowToInitUser(row, snapshotInitUser) {
  if (!row || typeof row !== 'object') return null;
  const pick = (...keys) => {
    for (const k of keys) {
      if (Object.prototype.hasOwnProperty.call(row, k) && row[k] !== undefined) {
        return row[k];
      }
    }
    return undefined;
  };
  const id = pick('id', 'Id');
  if (id === undefined || id === null) return null;
  const themeFallback =
  snapshotInitUser && typeof snapshotInitUser === 'object' && 'theme' in snapshotInitUser ?
  snapshotInitUser.theme :
  undefined;
  return {
    id,
    username: String(pick('username', 'Username') ?? ''),
    name: String(pick('name', 'Name') ?? ''),
    surname: String(pick('surname', 'Surname') ?? ''),
    phone: pick('phone', 'Phone') ?? '',
    email: pick('email', 'Email') ?? '',
    organization: pick('organization', 'Organization'),
    department: pick('department', 'Department') ?? '',
    type: pick('type', 'Type') ?? 0,
    is_verified: pick('is_verified', 'IsVerified') ?? false,
    create_date: pick('create_date', 'CreateDate'),
    organization_ids: pick('organization_ids', 'organizationIds', 'OrganizationIds'),
    basic_organization: pick('basic_organization', 'BasicOrganization'),
    theme: pick('theme', 'Theme') ?? themeFallback ?? 'light'
  };
}

/**
 * Bundle completo OfflineSync/organization-configs (`generatedAt` + `items`).
 * @returns {Promise<{ generatedAt?: string, items?: unknown[] } | null>}
 */
export async function getOfflineOrganizationConfigsBundle() {
  const record = await offlineDb.offlineData.get('organizationConfigsBundle');
  return record?.data ?? null;
}

/**
 * Config form offline: preferisce il nuovo bundle; fallback legacy `formConfig` (Auth/Init).
 * @returns {Promise<unknown | null>}
 */
export async function getOfflineFormConfig() {
  const bundle = await getOfflineOrganizationConfigsBundle();
  if (bundle != null) return bundle;
  const legacy = await offlineDb.offlineData.get('formConfig');
  return legacy?.data ?? null;
}

/**
 * Sceglie l'ID organizzazione per cui esiste `orgForm_*` in IndexedDB (form offline).
 * Allineato alla logica Home: org principale; se `-1`, basic org o prima voce in `organization_ids`.
 *
 * @param {Record<string, unknown> | null | undefined} initUser
 * @returns {number | null}
 */
export function resolveOfflineFormOrganizationId(initUser) {
  if (!initUser || typeof initUser !== 'object') return null;
  const toNum = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const mainOrg = toNum(initUser.organization);
  if (mainOrg !== null && mainOrg !== -1) return mainOrg;

  const basic = toNum(initUser.basic_organization);
  if (basic !== null && basic !== -1) return basic;

  const ids = initUser.organization_ids;
  if (Array.isArray(ids) && ids.length > 0) {
    const first = toNum(ids[0]);
    if (first !== null && first !== -1) return first;
  }
  return null;
}

/**
 * Config + defaults per una singola organizzazione (da sync offline).
 * @param {number|string} organizationId
 * @returns {Promise<{ configuration?: unknown, defaults?: unknown, organizationShortDescription?: string | null, organizationId?: number } | null>}
 */
export async function getOfflineOrganizationFormConfig(organizationId) {
  const record = await offlineDb.offlineData.get(`orgForm_${organizationId}`);
  return record?.data ?? null;
}

/**
 * @returns {Promise<number | null>}
 */
export async function getLastSyncTimestamp() {
  const record = await offlineDb.meta.get('lastOfflineSync');
  return record?.value != null ? Number(record.value) : null;
}